import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Form, Input, InputNumber, Select, Slider, Switch, ColorPicker, Typography, Divider, Space, Row, Col } from 'antd';
import { 
  MessageOutlined, 
  FontSizeOutlined, 
  BorderOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * DecorationNodeProperties - Configuration panel for decoration nodes
 * These are purely visual and don't affect workflow execution
 */

// Debounce utility
const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

// Helper hook to check if an input element is focused
const useInputFocusCheck = (inputRef) => {
  return useCallback(() => {
    const input = inputRef.current?.resizableTextArea?.textArea || inputRef.current?.input || inputRef.current;
    return input?.matches?.(':focus') || false;
  }, [inputRef]);
};

// Comment Node Properties
export const CommentNodeProperties = ({ config, updateConfig }) => {
  // Local state for text fields to prevent lag
  const [localTitle, setLocalTitle] = useState(config.title || '');
  const [localText, setLocalText] = useState(config.text || '');
  
  // Refs for input elements
  const titleInputRef = useRef(null);
  const textInputRef = useRef(null);
  
  // Check focus helpers
  const isTitleFocused = useInputFocusCheck(titleInputRef);
  const isTextFocused = useInputFocusCheck(textInputRef);
  
  // Sync local state when config changes from external source (only when not focused)
  useEffect(() => {
    if (config.title !== localTitle && !isTitleFocused()) {
      setLocalTitle(config.title || '');
    }
  }, [config.title]);

  useEffect(() => {
    if (config.text !== localText && !isTextFocused()) {
      setLocalText(config.text || '');
    }
  }, [config.text]);

  // Debounced update to parent
  const debouncedUpdateConfig = useDebounce(
    useCallback((field, value) => {
      updateConfig({
        config: {
          ...config,
          [field]: value
        }
      });
    }, [config, updateConfig]),
    300
  );

  // Handle title change
  const handleTitleChange = useCallback((e) => {
    const value = e.target.value;
    setLocalTitle(value);
    debouncedUpdateConfig('title', value);
  }, [debouncedUpdateConfig]);

  // Handle text change - update local immediately, debounce parent update
  const handleTextChange = useCallback((e) => {
    const value = e.target.value;
    setLocalText(value);
    debouncedUpdateConfig('text', value);
  }, [debouncedUpdateConfig]);

  // Non-text fields can update immediately (they don't have rapid input)
  const handleChange = useCallback((field, value) => {
    updateConfig({
      config: {
        ...config,
        [field]: value
      }
    });
  }, [config, updateConfig]);

  const handleColorChange = useCallback((field, color) => {
    const hexColor = typeof color === 'string' ? color : color?.toHexString?.() || color;
    handleChange(field, hexColor);
  }, [handleChange]);

  // Memoize styles
  const infoBoxStyle = useMemo(() => ({
    display: 'flex', 
    alignItems: 'center', 
    gap: 8, 
    marginBottom: 16,
    padding: '8px 12px',
    backgroundColor: '#fffbe6',
    borderRadius: 8
  }), []);

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={infoBoxStyle}>
        <MessageOutlined style={{ color: '#8c6800' }} />
        <Text style={{ color: '#8c6800' }}>
          Comment nodes are for documentation only and don't affect workflow execution.
        </Text>
      </div>

      <Form layout="vertical">
        <Form.Item label="Title" tooltip="Title will be displayed as header on the comment node">
          <Input
            ref={titleInputRef}
            value={localTitle}
            onChange={handleTitleChange}
            placeholder="Enter title (e.g., Note, TODO, Important)..."
            maxLength={50}
          />
        </Form.Item>

        <Form.Item label="Comment Text">
          <TextArea
            ref={textInputRef}
            value={localText}
            onChange={handleTextChange}
            placeholder="Enter your comment here..."
            autoSize={{ minRows: 3, maxRows: 10 }}
          />
        </Form.Item>

        <Divider>Style</Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Background Color">
              <ColorPicker
                value={config.backgroundColor || '#fffbe6'}
                onChange={(color) => handleColorChange('backgroundColor', color)}
                showText
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Border Color">
              <ColorPicker
                value={config.borderColor || '#ffe58f'}
                onChange={(color) => handleColorChange('borderColor', color)}
                showText
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Text Color">
          <ColorPicker
            value={config.textColor || '#8c6800'}
            onChange={(color) => handleColorChange('textColor', color)}
            showText
          />
        </Form.Item>
      </Form>
    </div>
  );
};

// Text Annotation Node Properties
export const TextAnnotationNodeProperties = ({ config, updateConfig }) => {
  // Local state for text to prevent lag
  const [localText, setLocalText] = useState(config.text || '');
  
  // Ref for input element
  const textInputRef = useRef(null);
  
  // Check focus helper
  const isTextFocused = useInputFocusCheck(textInputRef);
  
  // Sync local state when config changes from external source (only when not focused)
  useEffect(() => {
    if (config.text !== localText && !isTextFocused()) {
      setLocalText(config.text || '');
    }
  }, [config.text]);

  // Debounced update to parent
  const debouncedUpdateConfig = useDebounce(
    useCallback((field, value) => {
      updateConfig({
        config: {
          ...config,
          [field]: value
        }
      });
    }, [config, updateConfig]),
    300
  );

  // Handle text change - update local immediately, debounce parent update
  const handleTextChange = useCallback((e) => {
    const value = e.target.value;
    setLocalText(value);
    debouncedUpdateConfig('text', value);
  }, [debouncedUpdateConfig]);

  // Non-text fields can update immediately
  const handleChange = useCallback((field, value) => {
    updateConfig({
      config: {
        ...config,
        [field]: value
      }
    });
  }, [config, updateConfig]);

  const handleColorChange = useCallback((field, color) => {
    const hexColor = typeof color === 'string' ? color : color?.toHexString?.() || color;
    handleChange(field, hexColor);
  }, [handleChange]);

  // Memoize styles
  const infoBoxStyle = useMemo(() => ({
    display: 'flex', 
    alignItems: 'center', 
    gap: 8, 
    marginBottom: 16,
    padding: '8px 12px',
    backgroundColor: '#f0f5ff',
    borderRadius: 8
  }), []);

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={infoBoxStyle}>
        <FontSizeOutlined style={{ color: '#1890ff' }} />
        <Text style={{ color: '#1890ff' }}>
          Text annotations are labels for organizing your workflow.
        </Text>
      </div>

      <Form layout="vertical">
        <Form.Item label="Text">
          <Input
            ref={textInputRef}
            value={localText}
            onChange={handleTextChange}
            placeholder="Enter text..."
          />
        </Form.Item>

        <Divider>Typography</Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Font Size">
              <Slider
                min={10}
                max={48}
                value={config.fontSize || 16}
                onChange={(value) => handleChange('fontSize', value)}
                marks={{ 10: '10', 16: '16', 24: '24', 36: '36', 48: '48' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Font Weight">
              <Select
                value={config.fontWeight || 'normal'}
                onChange={(value) => handleChange('fontWeight', value)}
                style={{ width: '100%' }}
              >
                <Option value="normal">Normal</Option>
                <Option value="500">Medium</Option>
                <Option value="600">Semi-bold</Option>
                <Option value="bold">Bold</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Font Style">
              <Select
                value={config.fontStyle || 'normal'}
                onChange={(value) => handleChange('fontStyle', value)}
                style={{ width: '100%' }}
              >
                <Option value="normal">Normal</Option>
                <Option value="italic">Italic</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Text Align">
              <Select
                value={config.textAlign || 'left'}
                onChange={(value) => handleChange('textAlign', value)}
                style={{ width: '100%' }}
              >
                <Option value="left">Left</Option>
                <Option value="center">Center</Option>
                <Option value="right">Right</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Text Color">
          <ColorPicker
            value={config.textColor || '#262626'}
            onChange={(color) => handleColorChange('textColor', color)}
            showText
          />
        </Form.Item>
      </Form>
    </div>
  );
};

// Rectangle Shape Node Properties
export const ShapeRectangleNodeProperties = ({ config, updateConfig }) => {
  // Local state for all fields to prevent lag
  const [localText, setLocalText] = useState(config.text || '');
  const [localWidth, setLocalWidth] = useState(config.width || 200);
  const [localHeight, setLocalHeight] = useState(config.height || 120);
  const [localBorderWidth, setLocalBorderWidth] = useState(config.borderWidth || 2);
  const [localBorderRadius, setLocalBorderRadius] = useState(config.borderRadius || 8);
  
  // Refs for input elements
  const textInputRef = useRef(null);
  const widthInputRef = useRef(null);
  const heightInputRef = useRef(null);
  
  // Check focus helpers
  const isTextFocused = useInputFocusCheck(textInputRef);
  const isWidthFocused = useInputFocusCheck(widthInputRef);
  const isHeightFocused = useInputFocusCheck(heightInputRef);
  
  // Sync local state when config changes from external source (e.g., resize) - only when not focused
  useEffect(() => {
    if (config.text !== localText && !isTextFocused()) {
      setLocalText(config.text || '');
    }
  }, [config.text]);

  useEffect(() => {
    if (config.width !== localWidth && !isWidthFocused()) {
      setLocalWidth(config.width || 200);
    }
  }, [config.width]);

  useEffect(() => {
    if (config.height !== localHeight && !isHeightFocused()) {
      setLocalHeight(config.height || 120);
    }
  }, [config.height]);

  useEffect(() => {
    setLocalBorderWidth(config.borderWidth || 2);
  }, [config.borderWidth]);

  useEffect(() => {
    setLocalBorderRadius(config.borderRadius || 8);
  }, [config.borderRadius]);

  // Debounced update to parent
  const debouncedUpdateConfig = useDebounce(
    useCallback((field, value) => {
      updateConfig({
        config: {
          ...config,
          [field]: value
        }
      });
    }, [config, updateConfig]),
    150
  );

  // Handle text change - update local immediately, debounce parent update
  const handleTextChange = useCallback((e) => {
    const value = e.target.value;
    setLocalText(value);
    debouncedUpdateConfig('text', value);
  }, [debouncedUpdateConfig]);

  // Handle width change
  const handleWidthChange = useCallback((value) => {
    if (value !== null && value !== undefined) {
      setLocalWidth(value);
      debouncedUpdateConfig('width', value);
    }
  }, [debouncedUpdateConfig]);

  // Handle height change
  const handleHeightChange = useCallback((value) => {
    if (value !== null && value !== undefined) {
      setLocalHeight(value);
      debouncedUpdateConfig('height', value);
    }
  }, [debouncedUpdateConfig]);

  // Handle border width change
  const handleBorderWidthChange = useCallback((value) => {
    setLocalBorderWidth(value);
    debouncedUpdateConfig('borderWidth', value);
  }, [debouncedUpdateConfig]);

  // Handle border radius change
  const handleBorderRadiusChange = useCallback((value) => {
    setLocalBorderRadius(value);
    debouncedUpdateConfig('borderRadius', value);
  }, [debouncedUpdateConfig]);

  // Non-slider fields can update immediately
  const handleChange = useCallback((field, value) => {
    updateConfig({
      config: {
        ...config,
        [field]: value
      }
    });
  }, [config, updateConfig]);

  const handleColorChange = useCallback((field, color) => {
    const hexColor = typeof color === 'string' ? color : color?.toHexString?.() || color;
    handleChange(field, hexColor);
  }, [handleChange]);

  // Memoize styles
  const infoBoxStyle = useMemo(() => ({
    display: 'flex', 
    alignItems: 'center', 
    gap: 8, 
    marginBottom: 16,
    padding: '8px 12px',
    backgroundColor: '#e6f7ff',
    borderRadius: 8
  }), []);

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={infoBoxStyle}>
        <BorderOutlined style={{ color: '#1890ff' }} />
        <Text style={{ color: '#1890ff' }}>
          Use rectangles to visually group related nodes.
        </Text>
      </div>

      <Form layout="vertical">
        <Form.Item label="Label (Optional)">
          <Input
            ref={textInputRef}
            value={localText}
            onChange={handleTextChange}
            placeholder="Group label..."
          />
        </Form.Item>

        <Divider>Size</Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Width (px)">
              <InputNumber
                ref={widthInputRef}
                min={100}
                max={1200}
                step={10}
                value={localWidth}
                onChange={handleWidthChange}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Height (px)">
              <InputNumber
                ref={heightInputRef}
                min={60}
                max={800}
                step={10}
                value={localHeight}
                onChange={handleHeightChange}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider>Style</Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Background Color">
              <ColorPicker
                value={config.backgroundColor || 'rgba(24, 144, 255, 0.05)'}
                onChange={(color) => handleColorChange('backgroundColor', color)}
                showText
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Border Color">
              <ColorPicker
                value={config.borderColor || '#1890ff'}
                onChange={(color) => handleColorChange('borderColor', color)}
                showText
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Border Style">
              <Select
                value={config.borderStyle || 'dashed'}
                onChange={(value) => handleChange('borderStyle', value)}
                style={{ width: '100%' }}
              >
                <Option value="solid">Solid</Option>
                <Option value="dashed">Dashed</Option>
                <Option value="dotted">Dotted</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Border Width">
              <Slider
                min={1}
                max={5}
                value={localBorderWidth}
                onChange={handleBorderWidthChange}
                marks={{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Border Radius">
          <Slider
            min={0}
            max={24}
            value={localBorderRadius}
            onChange={handleBorderRadiusChange}
            marks={{ 0: '0', 8: '8', 16: '16', 24: '24' }}
          />
        </Form.Item>
      </Form>
    </div>
  );
};

export default {
  CommentNodeProperties,
  TextAnnotationNodeProperties,
  ShapeRectangleNodeProperties
};
